import { useState, useEffect } from "react";
import {
  saveTextInputsData,
  getparametersFromLocalStorage,
} from "../utils/storageUtils";

/**
 * Custom hook for managing TextInputs data state
 * Centralizes state management for all sections and provides data synchronization
 */
export const useTextInputsData = (
  initialparameters,
  onDataChange,
  onAddparameters
) => {
  // Initialize parameters data with proper defaults and localStorage fallback
  const initializeparametersData = () => {
    const defaults = {
      blood_pressure: "",
      bloodPressureUnit: "mmHg",
      pulse_rate: "",
      pulseRateUnit: "bpm",
      weight: "",
      weightUnit: "kg",
      spO2: "",
      spO2Unit: "%",
      height: "",
      heightUnit: "cm",
      temperature: "",
      temperatureUnit: "C",
    };

    // If initialparameters is provided, merge with defaults
    if (initialparameters) {
      return {
        ...defaults,
        ...initialparameters,
        // Ensure numeric values are strings for the input fields
        weight:
          initialparameters.weight !== undefined
            ? String(initialparameters.weight)
            : "",
        height:
          initialparameters.height !== undefined
            ? String(initialparameters.height)
            : "",
        temperature:
          initialparameters.temperature !== undefined
            ? String(initialparameters.temperature)
            : "",
        spO2:
          initialparameters.spO2 !== undefined
            ? String(initialparameters.spO2)
            : "",
        pulse_rate:
          initialparameters.pulse_rate !== undefined
            ? String(initialparameters.pulse_rate)
            : "",
      };
    }

    // Otherwise, try to load from localStorage
    const savedparameters = getparametersFromLocalStorage();
    if (savedparameters) {
      return {
        ...defaults,
        ...savedparameters,
        // Ensure numeric values are strings
        weight:
          savedparameters.weight !== undefined
            ? String(savedparameters.weight)
            : "",
        height:
          savedparameters.height !== undefined
            ? String(savedparameters.height)
            : "",
        temperature:
          savedparameters.temperature !== undefined
            ? String(savedparameters.temperature)
            : "",
        spO2:
          savedparameters.spO2 !== undefined
            ? String(savedparameters.spO2)
            : "",
        pulse_rate:
          savedparameters.pulse_rate !== undefined
            ? String(savedparameters.pulse_rate)
            : "",
      };
    }

    return defaults;
  };

  // parameters Data State
  const [parametersData, setparametersData] = useState(
    initializeparametersData
  );
  const [parametersList, setparametersList] = useState([]);

  // Allergy States - each medicine component maintains its own separate state
  const [medicines, setMedicines] = useState([]);
  const [food, setFood] = useState([]);
  const [other, setOther] = useState([]);
  const [newMedicine, setNewMedicine] = useState("");
  const [newFood, setNewFood] = useState("");
  const [newOther, setNewOther] = useState("");

  // Complaints State
  const [complaints, setComplaints] = useState([]);
  const [newComplaint, setNewComplaint] = useState({
    no: "",
    complaint: "",
    severity: "",
    duration: "",
    date: "",
  });
  const [severitySuggestions, setSeveritySuggestions] = useState([
    "Mild",
    "Moderate",
    "Severe",
    "Acute",
    "Chronic",
  ]);

  // Diagnosis State
  const [diagnosiss, setDiagnosiss] = useState([]);
  const [newDiagnosis, setNewDiagnosis] = useState({
    no: "",
    diagnosis: "",
    duration: "",
    date: "",
  });
  const [diagnosisSuggestions, setDiagnosisSuggestions] = useState([]);

  // Advice States
  const [advicesData, setAdvicesData] = useState([]);
  const [newAdvice, setNewAdvice] = useState({
    advice: "",
    details: "",
    date: new Date().toISOString().split("T")[0],
    id: null,
  });
  const [adviceSuggestions, setAdviceSuggestions] = useState([]);
  const [selectedAdviceId, setSelectedAdviceId] = useState("");

  // Clinical Findings States
  const [clinicalFindingsData, setClinicalFindingsData] = useState([]);
  const [newClinicalFinding, setNewClinicalFinding] = useState({
    finding: "",
  });

  const [PastHistoryData, setPastHistoryData] = useState([]);
  const [newPastHistory, setNewPastHistory] = useState({
    history: "",
  });

  // Update parametersData when initialparameters changes
  useEffect(() => {
    if (initialparameters && Object.keys(initialparameters).length > 0) {
      console.log(
        "Initial parameters received in useTextInputsData:",
        initialparameters
      );

      // Map the API response to the expected format
      const updatedparameters = {
        blood_pressure: initialparameters.blood_pressure || "",
        bloodPressureUnit: initialparameters.bloodPressureUnit || "mmHg",
        pulse_rate:
          initialparameters.pulse_rate !== undefined
            ? String(initialparameters.pulse_rate)
            : "",
        pulseRateUnit: initialparameters.pulseRateUnit || "bpm",
        weight:
          initialparameters.weight !== undefined
            ? String(initialparameters.weight)
            : "",
        weightUnit: initialparameters.weightUnit || "kg",
        spO2:
          initialparameters.spO2 !== undefined
            ? String(initialparameters.spO2)
            : "",
        spO2Unit: initialparameters.spO2Unit || "%",
        height:
          initialparameters.height !== undefined
            ? String(initialparameters.height)
            : "",
        heightUnit: initialparameters.heightUnit || "cm",
        temperature:
          initialparameters.temperature !== undefined
            ? String(initialparameters.temperature)
            : "",
        temperatureUnit: initialparameters.temperatureUnit || "C",
        // Add any other fields that might be in the API response
        ...(initialparameters.blood_sugar !== undefined && {
          blood_sugar: String(initialparameters.blood_sugar),
        }),
        ...(initialparameters.bloodSugarUnit && {
          bloodSugarUnit: initialparameters.bloodSugarUnit,
        }),
      };

      console.log(
        "Mapped parameters data in useTextInputsData:",
        updatedparameters
      );
      setparametersData((prev) => ({
        ...prev,
        ...updatedparameters,
      }));
    }
  }, [initialparameters]);

  // Notify parent component when data changes
  useEffect(() => {
    const currentData = {
      complaints: complaints,
      diagnosis: diagnosiss,
      medicines: medicines,
      food: food,
      other: other,
      advice: advicesData,
      clinical_findings: clinicalFindingsData,
      PastHistory:PastHistoryData,
      parameters: parametersData,
    };

    // Store in localStorage for parent component access
    saveTextInputsData(currentData);

    // Also notify parent via callback
    if (onDataChange) {
      onDataChange(currentData);
    }
  }, [
    complaints,
    diagnosiss,
    medicines,
    food,
    other,
    advicesData,
    clinicalFindingsData,
    parametersData,
    PastHistoryData,
    onDataChange,
  ]);

  return {
    // parameters
    parametersData,
    setparametersData,
    parametersList,
    setparametersList,

    // Allergies
    medicines,
    setMedicines,
    food,
    setFood,
    other,
    setOther,
    newMedicine,
    setNewMedicine,
    newFood,
    setNewFood,
    newOther,
    setNewOther,

    // Complaints
    complaints,
    setComplaints,
    newComplaint,
    setNewComplaint,
    severitySuggestions,
    setSeveritySuggestions,

    // Diagnosis
    diagnosiss,
    setDiagnosiss,
    newDiagnosis,
    setNewDiagnosis,
    diagnosisSuggestions,
    setDiagnosisSuggestions,

    // Advice
    advicesData,
    setAdvicesData,
    newAdvice,
    setNewAdvice,
    adviceSuggestions,
    setAdviceSuggestions,
    selectedAdviceId,
    setSelectedAdviceId,

    // Clinical Findings
    clinicalFindingsData,
    setClinicalFindingsData,
    newClinicalFinding,
    setNewClinicalFinding,

    // Past history
    PastHistoryData,
    setPastHistoryData,
    newPastHistory,
    setNewPastHistory,
  };
};
